import { parseNumberInput } from "@/lib/parse-number";
import {
  mean,
  type QuantileMethod,
  quantile,
  standardDeviation,
} from "@/lib/stats/descriptive";
import {
  isMissingToken,
  parseTable,
} from "@/lib/tool-kit/research-design/matrix";

/**
 * Delphi round statistics and consensus rules.
 *
 * A Delphi study has no single definition of consensus. A systematic review of 100 Delphi
 * studies found that about 98 claimed to assess consensus but only 72 defined it; percent
 * agreement was the most common definition, with a median threshold of 75% (Diamond et
 * al., 2014). Interquartile-range rules are the other common family (reviewed in von der
 * Gracht, 2012). So this module computes the statistics once — n, median, quartiles, IQR,
 * mean, the shares rating in an "agree" and a "disagree" band — and applies whichever rules
 * the researcher switches on, with every threshold their own. Nothing here is a standard;
 * the defaults are conventions the page cites.
 */

export const MAX_ROUNDS = 5;

export interface DelphiRules {
  /** Rating scale 1…scale. */
  scale: number;
  /** Ratings at or above this count as agreement. */
  agreeFrom: number;
  /** Ratings at or below this count as disagreement. */
  disagreeTo: number;
  percentOn: boolean;
  /** Percent of panelists, 0–100. */
  percentThreshold: number;
  iqrOn: boolean;
  iqrThreshold: number;
  medianOn: boolean;
  /** Agreement needs a median at or above this. */
  medianMin: number;
  stabilityOn: boolean;
  /** Largest change, in percentage points, in the winning share between rounds. */
  stabilityMax: number;
  method: QuantileMethod;
}

/** Common starting points for each scale length; every one can be changed. */
export function defaultBands(scale: number): {
  agreeFrom: number;
  disagreeTo: number;
  medianMin: number;
} {
  switch (scale) {
    case 4:
      return { agreeFrom: 3, disagreeTo: 2, medianMin: 3 };
    case 5:
      return { agreeFrom: 4, disagreeTo: 2, medianMin: 4 };
    case 7:
      return { agreeFrom: 6, disagreeTo: 2, medianMin: 6 };
    case 9:
      return { agreeFrom: 7, disagreeTo: 3, medianMin: 7 };
    default:
      return { agreeFrom: 8, disagreeTo: 3, medianMin: 8 };
  }
}

export interface ItemRound {
  /** Valid ratings. */
  n: number;
  median: number;
  q1: number;
  q3: number;
  iqr: number;
  mean: number;
  sd: number | null;
  /** Percent of valid ratings in the agree band. */
  agree: number;
  /** Percent of valid ratings in the disagree band. */
  disagree: number;
}

export function summarizeRatings(
  values: readonly number[],
  rules: Pick<DelphiRules, "agreeFrom" | "disagreeTo" | "method">
): ItemRound | null {
  const n = values.length;
  if (n === 0) {
    return null;
  }
  const q1 = quantile(values, 0.25, rules.method);
  const q3 = quantile(values, 0.75, rules.method);
  return {
    n,
    median: quantile(values, 0.5, rules.method),
    q1,
    q3,
    iqr: q3 - q1,
    mean: mean(values),
    sd: n > 1 ? standardDeviation(values) : null,
    agree: (values.filter((v) => v >= rules.agreeFrom).length / n) * 100,
    disagree: (values.filter((v) => v <= rules.disagreeTo).length / n) * 100,
  };
}

export type Verdict = "agree" | "disagree" | "none";

export type RuleKey = "percent" | "iqr" | "median" | "stability";

export interface Judgement {
  verdict: Verdict;
  /** Rules that were switched on and not met (empty when consensus). */
  unmet: RuleKey[];
  /** The direction that was tested, even when consensus fell short. */
  direction: Verdict;
}

const EPS = 1e-9;

function directionOf(round: ItemRound, rules: DelphiRules): Verdict {
  if (rules.percentOn) {
    if (round.agree >= round.disagree) {
      return round.agree > 0 ? "agree" : "none";
    }
    return "disagree";
  }
  if (round.median >= rules.agreeFrom) {
    return "agree";
  }
  return round.median <= rules.disagreeTo ? "disagree" : "none";
}

function share(round: ItemRound, direction: Verdict): number {
  return direction === "disagree" ? round.disagree : round.agree;
}

/**
 * Apply the switched-on rules to one item in one round. `previous` is the same item's
 * previous round, for the stability rule; without one (round 1, or no ratings then) that
 * rule cannot be confirmed and counts as unmet.
 */
export function judge(
  round: ItemRound | null,
  previous: ItemRound | null,
  rules: DelphiRules
): Judgement {
  if (round === null) {
    return { verdict: "none", unmet: [], direction: "none" };
  }
  const direction = directionOf(round, rules);
  const unmet: RuleKey[] = [];
  if (direction === "none") {
    // Nothing to be in consensus about: report whichever rules are on as unmet.
    if (rules.percentOn) {
      unmet.push("percent");
    }
  } else if (
    rules.percentOn &&
    share(round, direction) + EPS < rules.percentThreshold
  ) {
    unmet.push("percent");
  }
  if (rules.iqrOn && round.iqr > rules.iqrThreshold + EPS) {
    unmet.push("iqr");
  }
  if (
    rules.medianOn &&
    direction === "agree" &&
    round.median < rules.medianMin
  ) {
    unmet.push("median");
  }
  if (rules.stabilityOn) {
    const stable =
      previous !== null &&
      direction !== "none" &&
      Math.abs(share(round, direction) - share(previous, direction)) <=
        rules.stabilityMax + EPS;
    if (!stable) {
      unmet.push("stability");
    }
  }
  const anyRule =
    rules.percentOn || rules.iqrOn || rules.medianOn || rules.stabilityOn;
  const met = anyRule && direction !== "none" && unmet.length === 0;
  return { verdict: met ? direction : "none", unmet, direction };
}

/* ------------------------------------------------------------------ reading rounds */

export interface RoundData {
  /** Item names from the header row, or null. */
  columns: string[] | null;
  /** One row per panelist: a rating, or null for a blank cell. */
  ratings: (number | null)[][];
  /** Cells that held something that is not a number on the scale. */
  invalid: number;
}

function isValue(cell: string): boolean {
  return isMissingToken(cell) || parseNumberInput(cell) !== null;
}

export function parseRound(
  text: string,
  scale: number,
  header: boolean,
  ids: boolean
): RoundData {
  const table = parseTable(text, isValue, { header, ids });
  let invalid = 0;
  const ratings = table.body.map((row) =>
    row.map((cell) => {
      if (isMissingToken(cell)) {
        return null;
      }
      const value = parseNumberInput(cell);
      if (value === null || value < 1 || value > scale) {
        invalid += 1;
        return null;
      }
      return value;
    })
  );
  return { columns: table.columns, ratings, invalid };
}

export interface ItemResult {
  name: string;
  /** One entry per round; null where the round has no ratings for the item. */
  rounds: (ItemRound | null)[];
  judgements: Judgement[];
}

export interface DelphiResult {
  items: ItemResult[];
  /** Rounds with data, as indexes into the input. */
  roundCount: number;
  /** Per round: how many items reached consensus (either way). */
  consensusCounts: number[];
  /** Round numbers (from 1) whose item count differs from the item list's. */
  mismatched: number[];
}

export function analyzeDelphi(
  rounds: readonly RoundData[],
  names: readonly string[],
  rules: DelphiRules
): DelphiResult {
  const numbered = rounds.map((round, index) => ({ round, number: index + 1 }));
  const withData = numbered.filter(({ round }) => round.ratings.length > 0);
  const used = withData.map(({ round }) => round);
  const itemCount = names.length;
  const items: ItemResult[] = names.map((name, column) => {
    const summaries = used.map((round) =>
      summarizeRatings(
        round.ratings.flatMap((row) => {
          const value = row[column];
          return value === null || value === undefined ? [] : [value];
        }),
        rules
      )
    );
    return {
      name,
      rounds: summaries,
      judgements: summaries.map((summary, index) =>
        judge(summary, index > 0 ? summaries[index - 1] : null, rules)
      ),
    };
  });
  const consensusCounts = used.map(
    (_, index) =>
      items.filter((item) => item.judgements[index].verdict !== "none").length
  );
  const mismatched = withData.flatMap(({ round, number }) =>
    round.ratings.some((row) => row.length !== itemCount) ? [number] : []
  );
  return { items, roundCount: used.length, consensusCounts, mismatched };
}
